@if (failed) {
  <cs-info-card type="warning" [dismissible]="false">
    {{ LL.trainingOverview.loadFailed() }}
    <cs-button tertiary (click)="retry.emit()">{{ LL.trainingOverview.retry() }}</cs-button>
  </cs-info-card>
} @else if (!model) {
  <!-- no model yet: a spinner only while a load is really running -->
  @if (busy) {
    <div class="overview-loading">
      <cs-spinner></cs-spinner>
    </div>
  }
} @else {
  <div class="overview" [class.reloading]="busy">

    <!-- what the signs and tags mean -->
    <div class="legend">
      <span class="legend-item"><span class="sign same">{{ LL.trainingOverview.markSame() }}</span>{{ LL.trainingOverview.legend.same() }}</span>
      <span class="legend-item"><span class="sign down">{{ LL.trainingOverview.markDown() }}</span>{{ LL.trainingOverview.legend.down() }}</span>
      <span class="legend-item">
        <span class="tag">{{ LL.trainingOverview.tags.heavy() }} / {{ LL.trainingOverview.tags.medium() }} / {{ LL.trainingOverview.tags.light() }}</span>
        {{ LL.trainingOverview.legend.intensity() }}
      </span>
      <span class="legend-item"><span class="tag">{{ LL.trainingOverview.tags.test() }}</span>{{ LL.trainingOverview.legend.test() }}</span>
      <span class="legend-item"><span class="tag">{{ LL.trainingOverview.tags.deload() }}</span>{{ LL.trainingOverview.legend.deload() }}</span>
      <span class="legend-item"><span class="tag">{{ LL.trainingOverview.tags.break() }}</span>{{ LL.trainingOverview.legend.break() }}</span>
      <span class="legend-item"><span class="tag">{{ LL.trainingOverview.tags.cap() }}</span>{{ LL.trainingOverview.legend.cap() }}</span>
    </div>

    <!-- the host's own notes (for example "older workouts are still downloading") -->
    <ng-content></ng-content>

    @if (hasNoSessions) {
      <div class="empty-text no-workouts">{{ LL.trainingOverview.noWorkouts({ weeks: loadedWeeks }) }}</div>
    } @else {
      @for (section of model.sections; track $index; let sectionIndex = $index) {
        <section class="overview-section" [attr.data-plan-reference]="section.planReference ?? 'other'">
          <div class="section-head">
            @if (section.badge) {
              <span class="badge">{{ section.badge }}</span>
            }
            <span class="section-title">{{ section.title }}</span>
            @if (section.exerciseNamesText) {
              <span class="section-names">{{ section.exerciseNamesText }}</span>
            }
            @if (section.columns.length > 0) {
              <span class="section-count wide-only">
                {{ LL.trainingOverview.showingSessions({ shown: section.columns.length, total: section.sessionCount }) }}
              </span>
            }
            @if (canShowEarlier[sectionIndex]) {
              <cs-button tertiary class="show-earlier wide-only" (click)="showEarlier.emit(sectionIndex)">
                {{ LL.trainingOverview.showEarlier({ count: sessionsStep }) }}
              </cs-button>
            }
          </div>

          @if (section.columns.length === 0) {
            <div class="empty-text">{{ LL.trainingOverview.noSessions() }}</div>
          } @else {

            <!-- wide screens: one table, the newest session on the right -->
            <div class="table-box wide-only">
              <div class="table-scroll" #scroller [attr.data-section]="sectionIndex">
                <table>
                  <thead>
                    <tr>
                      <th class="col-exercise">{{ LL.trainingOverview.columnExercise() }}</th>
                      <th class="col-sets">{{ LL.trainingOverview.columnSets() }}</th>
                      <th class="col-target">{{ LL.trainingOverview.columnRepTarget() }}</th>
                      @for (column of section.columns; track column.workoutId) {
                        <th class="col-session" [attr.data-date]="column.dateIso" [attr.data-workout-id]="column.workoutId">{{ formatDate(column.dateIso) }}</th>
                      }
                      <!-- empty, sized by the component so whole columns fill the box; see placeTable -->
                      <th class="col-spacer"></th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (row of section.programRows; track row.exerciseId) {
                      <ng-container *ngTemplateOutlet="tableRow; context: { row: row, section: section }"></ng-container>
                    }
                    @if (section.greyRows.length > 0) {
                      @if (section.programRows.length > 0) {
                        <tr class="sub-heading">
                          <td class="col-exercise" colspan="3">{{ LL.trainingOverview.noLongerInWorkout() }}</td>
                          <td [attr.colspan]="section.columns.length + 1"></td>
                        </tr>
                      }
                      @for (row of section.greyRows; track row.exerciseId) {
                        <ng-container *ngTemplateOutlet="tableRow; context: { row: row, section: section }"></ng-container>
                      }
                    }
                  </tbody>
                </table>
              </div>
            </div>

            <!-- phones: a card per exercise with its newest few sessions -->
            <div class="phone-list phone-only">
              @for (row of section.programRows; track row.exerciseId) {
                <ng-container *ngTemplateOutlet="phoneCard; context: { row: row }"></ng-container>
              }
              @if (section.greyRows.length > 0) {
                @if (section.programRows.length > 0) {
                  <div class="phone-sub-heading">{{ LL.trainingOverview.noLongerInWorkout() }}</div>
                }
                @for (row of section.greyRows; track row.exerciseId) {
                  <ng-container *ngTemplateOutlet="phoneCard; context: { row: row }"></ng-container>
                }
              }
            </div>
          }
        </section>
      }
    }
  </div>
}

<!-- one exercise's row in the table -->
<ng-template #tableRow let-row="row" let-section="section">
  <tr [class.grey-row]="!row.isInProgram" [attr.data-exercise-id]="row.exerciseId">
    <td class="col-exercise">
      @if (isTappable(row)) {
        <button type="button" class="row-button" (click)="onRowTap(row, $event)">{{ row.name }}</button>
      } @else {
        <span class="row-name">{{ row.name }}</span>
      }
    </td>
    <td class="col-sets">{{ row.setsText }}</td>
    <td class="col-target">{{ row.repTargetText }}</td>
    @for (cell of row.cells; track $index) {
      <td class="col-session"
        [class.mark-same]="cell.mark === OverviewMark.Same"
        [class.mark-down]="cell.mark === OverviewMark.Down"
        [class.tag-test]="cell.sessionTag === OverviewSessionTag.Test"
        [attr.data-date]="section.columns[$index]?.dateIso" [attr.data-workout-id]="section.columns[$index]?.workoutId">
        @if (cell.kind === OverviewCellKind.Skipped) {
          <span class="skipped">{{ LL.trainingOverview.skipped() }}</span>
        } @else if (cell.kind === OverviewCellKind.Trained) {
          @if (markText(cell)) {<span class="sign">{{ markText(cell) }}</span>}
          @if (intensityText(cell)) {<span class="tag">{{ intensityText(cell) }}</span>}
          @if (sessionTagText(cell)) {<span class="tag">{{ sessionTagText(cell) }}</span>}
          @if (cell.weightText) {
            <span class="weight">{{ cell.weightText }}</span><span class="times">×</span><span class="reps">{{ cell.repsText }}</span>
          } @else {
            <span class="reps">{{ cell.setsText }}</span>
          }
        }
      </td>
    }
    <td class="col-spacer"></td>
  </tr>
</ng-template>

<!-- one exercise's card on a phone -->
<ng-template #phoneCard let-row="row">
  <div class="phone-card" [class.grey-row]="!row.isInProgram" [class.tappable]="isTappable(row)"
    [attr.data-exercise-id]="row.exerciseId"
    [attr.role]="isTappable(row) ? 'button' : null" [attr.tabindex]="isTappable(row) ? 0 : null"
    (click)="onRowTap(row, $event)" (keydown.enter)="onRowTap(row, $event)">
    <div class="phone-card-head">
      <span class="row-name">{{ row.name }}</span>
      @if (phoneTargetsText(row)) {
        <span class="phone-targets">{{ phoneTargetsText(row) }}</span>
      }
    </div>
    <!-- one row per session, newest first: the date and its tags on the left, the sets on the right.
         Sets at different weights each get a line of their own, so nothing has to break mid-word -->
    <div class="phone-sessions">
      @for (entry of phoneCells(row); track entry.dateIso + $index) {
        <div class="phone-session"
          [class.mark-same]="entry.cell.mark === OverviewMark.Same"
          [class.mark-down]="entry.cell.mark === OverviewMark.Down"
          [class.tag-test]="entry.cell.sessionTag === OverviewSessionTag.Test">
          <div class="phone-session-when">
            <span class="phone-session-date">{{ formatDate(entry.dateIso) }}</span>
            @if (intensityText(entry.cell)) {<span class="tag">{{ intensityText(entry.cell) }}</span>}
            @if (sessionTagText(entry.cell)) {<span class="tag">{{ sessionTagText(entry.cell) }}</span>}
          </div>
          <div class="phone-session-sets">
            @if (entry.cell.kind === OverviewCellKind.Skipped) {
              <span class="skipped">{{ LL.trainingOverview.skipped() }}</span>
            } @else {
              @if (markText(entry.cell)) {<span class="sign">{{ markText(entry.cell) }}</span>}
              <span class="set-lines">
                @if (entry.cell.weightText) {
                  <span class="set-line"><span class="weight">{{ entry.cell.weightText }}</span><span class="times">×</span><span class="reps">{{ entry.repsText }}</span></span>
                } @else {
                  @for (line of entry.setLines; track $index) {
                    <span class="set-line reps">{{ line }}</span>
                  }
                }
              </span>
            }
          </div>
        </div>
      } @empty {
        <div class="phone-no-sessions">{{ LL.trainingOverview.noSessions() }}</div>
      }
    </div>
  </div>
</ng-template>
